// "How to improve" for each report card grade: what happened (the trades and days behind the
// grade), what to do about it (steps with the trader's own numbers in them), the target for
// next time, and one-click actions (add a rule, test it as an experiment, open a tool).
// Everything comes from the same numbers the grade was given from (weekStats in weeklyGrades.js).
import { formatMoney, usd } from './pnl';
import { currentLocale, translateNow as tn } from './langState';

const round10 = (n) => Math.max(10, Math.round(Math.abs(n) / 10) * 10);
const dayLabel = (day) => new Date(`${day}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
const tradeLabel = (t) => `${new Date(t.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })} · ${t.symbol}`;
const pct = (n) => `${Math.round(n)}%`;

// Profit factor needed for each Results grade (weeklyGrades.js gradeResults).
const PF_STEPS = [[1, 'C−'], [1.2, 'C+'], [1.5, 'B'], [2, 'A−'], [2.5, 'A+']];

function results(s, ctx) {
  const evidence = [
    { label: tn('Win rate'), value: pct(s.winRate) },
    { label: tn('Average win / average loss'), value: `${usd.format(s.avgWin)} / ${usd.format(s.avgLoss)}` },
  ];
  if (s.bestDay && s.bestDay.net > 0) evidence.push({ label: tn('Best day'), value: `${dayLabel(s.bestDay.day)} · ${formatMoney(s.bestDay.net)}`, tone: 'gain' });
  if (s.worstDay && s.worstDay.net < 0) evidence.push({ label: tn('Worst day'), value: `${dayLabel(s.worstDay.day)} · ${formatMoney(s.worstDay.net)}`, tone: 'loss' });

  const steps = [];
  const actions = [];
  const payoff = s.avgLoss ? s.avgWin / s.avgLoss : 0;
  const breakEven = s.avgWin + s.avgLoss ? (s.avgLoss / (s.avgWin + s.avgLoss)) * 100 : 0;
  if (s.avgLoss && payoff < 1) {
    steps.push(tn('Your average win ({win}) is smaller than your average loss ({loss}), so you need to win {be} of trades just to break even. Let winners run to at least {target} (1.5× your average loss) before taking profit.',
      { win: usd.format(s.avgWin), loss: usd.format(s.avgLoss), be: pct(breakEven), target: usd.format(s.avgLoss * 1.5) }));
    actions.push({ kind: 'rule', text: tn('My target is at least 1.5× my stop. If it isn’t there, I skip the trade.') });
  } else if (s.avgLoss && s.winRate < breakEven + 5) {
    steps.push(tn('Your winners are bigger than your losers, but at this payoff you need a {be} win rate to break even and you won {rate}. Take fewer, better trades: only your A setup.',
      { be: pct(breakEven), rate: pct(s.winRate) }));
  }
  const worstShare = s.grossLoss && s.worstDay && s.worstDay.net < 0 ? -s.worstDay.net / s.grossLoss : 0;
  if (worstShare >= 0.4) {
    const limit = round10(s.typicalLoss * 2.5);
    steps.push(tn('{day} alone was {share} of all your losses. A daily loss limit of about {limit} (2–3 typical losses) would have ended that day early.',
      { day: dayLabel(s.worstDay.day), share: pct(worstShare * 100), limit: usd.format(limit) }));
    actions.push({ kind: 'rule', text: tn('When I’m down {limit} on the day, I stop trading.', { limit: usd.format(limit) }) });
  }
  steps.push(ctx.month ? tn('Keep trading the setup and the hours that made your best day. Leave everything else out next month.')
    : tn('Keep trading the setup and the hours that made your best day. Leave everything else out next week.'));
  actions.push({ kind: 'link', to: '/app/playbook', label: tn('Open my Playbook') });

  const pf = s.profitFactor;
  const next = s.net < 0 ? null : PF_STEPS.find(([need]) => pf < need);
  const target = s.net < 0
    ? tn('Finish green. That alone lifts Results to at least a C−.')
    : next ? tn('A profit factor of {pf} or higher gets Results to {grade}.', { pf: next[0].toFixed(1), grade: next[1] })
      : tn('You’re at the top grade. Protect it with the same size and the same setups.');
  return { evidence, steps, target, actions };
}

function risk(s, ctx) {
  const cap = round10(s.typicalLoss * 1.5);
  const evidence = s.outsizedTrades.slice(0, 5).map(({ t, net }) => ({
    label: tradeLabel(t), value: tn('{amount} · {ratio}× your typical loss', { amount: formatMoney(net), ratio: (net / s.typicalLoss).toFixed(1) }), tone: 'loss',
  }));
  if (!evidence.length && s.lossCount) evidence.push({ label: tn('Typical loss'), value: formatMoney(s.typicalLoss) });
  const steps = [];
  const actions = [];
  if (s.outsizedTrades.length) {
    const extra = s.outsizedTrades.reduce((sum, x) => sum + (x.net - s.typicalLoss), 0);
    steps.push(tn('Had these losses stopped at your typical size, you’d have kept about {amount}.', { amount: usd.format(-extra) }));
  }
  steps.push(tn('Cap every trade at about {cap} (1.5× your typical loss of {typical}). Work out the contracts from your stop distance before you enter.',
    { cap: usd.format(cap), typical: usd.format(-s.typicalLoss) }));
  steps.push(tn('Place the stop in the platform as a bracket order the moment you enter, and never move it further away.'));
  if (s.worstDay && s.worstDay.net < 0 && s.grossLoss && -s.worstDay.net / s.grossLoss > 0.4) {
    steps.push(tn('{day} was {amount}. Stop for the day at about {limit}.', { day: dayLabel(s.worstDay.day), amount: formatMoney(s.worstDay.net), limit: usd.format(round10(s.typicalLoss * 2.5)) }));
  }
  actions.push({ kind: 'rule', text: tn('Every trade has a stop in the platform before I enter, and I never move it further away.') });
  actions.push({ kind: 'link', to: '/tools/position-size', label: tn('Position size calculator') });
  const target = ctx.month
    ? tn('Next month: no loss bigger than {cap}. Zero losses over 1.5× typical is an A.', { cap: usd.format(cap) })
    : tn('Next week: no loss bigger than {cap}. Zero losses over 1.5× typical is an A.', { cap: usd.format(cap) });
  return { evidence, steps, target, actions };
}

function discipline(s, ctx) {
  const evidence = [];
  for (const d of s.brokeDays.slice(0, 4)) {
    const net = (s.dayNets.find((x) => x.day === d) || {}).net || 0;
    evidence.push({ label: dayLabel(d), value: tn('broke a rule · {amount}', { amount: formatMoney(net) }), tone: net < 0 ? 'loss' : '' });
  }
  for (const m of s.mistakeTags.slice(0, 3)) {
    evidence.push({ label: `"${m.tag}"`, value: tn('{n} trades · {amount}', { n: m.count, amount: formatMoney(m.net) }), tone: m.net < 0 ? 'loss' : '' });
  }
  const steps = [];
  const actions = [];
  const top = s.ruleBreaks[0];
  if (top) {
    steps.push(tn('“{rule}” is the rule you break most ({n} of {days} checked days). Rewrite it as a yes/no you can check before each entry, and put it first in your list.',
      { rule: top.rule, n: top.days, days: s.checkedDays.length }));
  }
  const worstTag = s.mistakeTags.find((m) => m.net < 0);
  if (worstTag) {
    steps.push(tn('“{tag}” cost you {amount} over {n} trades. Before every entry, ask: is this a {tag}? If yes, skip it.',
      { tag: worstTag.tag, amount: usd.format(-worstTag.net), n: worstTag.count }));
    actions.push({ kind: 'rule', text: tn('No “{tag}” trades. If I spot one, I skip it.', { tag: worstTag.tag }) });
    actions.push({ kind: 'test', metric: { kind: 'tag', key: worstTag.tag }, area: tn('Mistakes'), tip: tn('No “{tag}” trades for 10 days', { tag: worstTag.tag }) });
  }
  steps.push(tn('Read your rules out loud in Morning prep, and end the day the first time you break one.'));
  if (!s.checkedDays.length && !ctx.noJournal) steps.push(tn('Tick your rules in the End-of-day reflection every day, so this grade can track them.'));
  actions.push({ kind: 'link', to: '/app/morning', label: tn('Open Morning prep') });
  const target = ctx.month ? tn('Next month: every rule followed on every day, and zero mistake trades.') : tn('Next week: every rule followed on every day, and zero mistake trades.');
  return { evidence, steps, target, actions };
}

function emotions(s, ctx) {
  const byDay = new Map();
  for (const { t, net } of s.afterTwo) {
    const day = t.date ? new Date(t.date) : null;
    const key = day ? day.toDateString() : '';
    const e = byDay.get(key) || { t, n: 0, net: 0 };
    e.n += 1; e.net += net;
    byDay.set(key, e);
  }
  const evidence = [...byDay.values()].sort((a, b) => a.net - b.net).slice(0, 4).map((e) => ({
    label: new Date(e.t.date).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' }),
    value: tn('{n} trades after 2 losses · {amount}', { n: e.n, amount: formatMoney(e.net) }), tone: e.net < 0 ? 'loss' : '',
  }));
  if (!ctx.noJournal) {
    for (const [emotion, n] of s.emotionCounts.slice(0, 2)) evidence.push({ label: tn(emotion), value: tn(n === 1 ? 'on {n} day' : 'on {n} days', { n }) });
  }
  const steps = [];
  const actions = [];
  if (s.afterTwo.length) {
    steps.push(tn('Without the trades you took after 2 losses, you’d be {amount} better off.', { amount: usd.format(Math.max(0, -s.afterTwoNet)) }));
  }
  steps.push(tn('Two losses in a day ends the day, on every account. Close the platform and write your review.'));
  steps.push(tn('After any loss, stand up and wait 15 minutes before the next trade. In Live session, tap a reset button to start the timer.'));
  if (!ctx.noJournal && s.emotionCounts.length) {
    steps.push(tn('{emotion} showed up most. On mornings your check-in shows it, trade half size.', { emotion: tn(s.emotionCounts[0][0]) }));
  }
  const twoLoss = tn('Stop trading after 2 losses in a row.');
  if (!(ctx.rules || []).some((r) => /2|two|dos/i.test(r) && /(loss|pérdida)/i.test(r))) actions.push({ kind: 'rule', text: twoLoss });
  actions.push({ kind: 'test', metric: { kind: 'afterLoss' }, area: tn('Tilt'), tip: tn('A 15-minute break after every loss') });
  actions.push({ kind: 'link', to: '/app/session', label: tn('Open Live session') });
  const target = ctx.month ? tn('Next month: zero trades after 2 losses in a day.') : tn('Next week: zero trades after 2 losses in a day.');
  return { evidence, steps, target, actions };
}

function journaling(s, ctx) {
  const evidence = [];
  if (s.untagged) evidence.push({ label: tn('Trades without a setup tag'), value: tn('{n} of {total}', { n: s.untagged, total: s.count }), tone: 'loss' });
  if (s.noPrepDays.length) evidence.push({ label: tn('Days without morning prep'), value: s.noPrepDays.slice(0, 4).map(dayLabel).join(', ') });
  if (s.noReflectDays.length) evidence.push({ label: tn('Days without a reflection'), value: s.noReflectDays.slice(0, 4).map(dayLabel).join(', ') });
  const steps = [];
  if (s.untagged) steps.push(tn('Tag each trade with its setup right after you import. It takes a minute, and it’s what lets the Playbook and Insights tell your good setups from your bad ones.'));
  if (s.noPrepDays.length) steps.push(tn('Do the 2-minute Morning prep before the open: check-in, rules and today’s plan. Turn on the morning reminder email so you don’t forget.'));
  if (s.noReflectDays.length) steps.push(tn('Write two lines in the End-of-day reflection the same day, before you look at your P&L.'));
  if (!steps.length) steps.push(tn('Your journal is complete. Keep it that way; every other grade depends on it.'));
  const actions = [
    { kind: 'link', to: '/app', label: tn('Tag my trades') },
    { kind: 'link', to: '/app/settings', label: tn('Turn on reminders') },
  ];
  const target = tn('Every trade tagged, and prep and reflection on every trading day.');
  return { evidence, steps, target, actions };
}

function focus(s, ctx, names) {
  const solid = s.markets.filter((m) => m.count >= 3);
  const best = [...solid].sort((a, b) => b.net - a.net)[0];
  const evidence = [...s.markets].sort((a, b) => b.net - a.net).slice(0, 5).map((m) => ({
    label: names(m.market), value: tn('{n} trades · {amount}', { n: m.count, amount: formatMoney(m.net) }), tone: m.net >= 0 ? 'gain' : 'loss',
  }));
  const steps = [];
  const actions = [];
  const losing = solid.filter((m) => m.net < 0 && m !== best);
  if (best && best.net > 0 && losing.length) {
    const lost = losing.reduce((sum, m) => sum + m.net, 0);
    steps.push(tn('Without {markets}, you’d have kept {amount}.', { markets: losing.map((m) => names(m.market)).join(', '), amount: usd.format(-lost) }));
    steps.push(tn('Trade only {market} for the next 2 weeks. If you want to keep learning the others, paper-trade them.', { market: names(best.market) }));
    actions.push({ kind: 'rule', text: tn('I only trade {market} until I’m consistently green in it.', { market: names(best.market) }) });
  } else if (s.markets.length > 1) {
    steps.push(tn('Pick one main market and trade it for a full week, so your stats show what you’re really good at.'));
  } else {
    steps.push(tn('One market, full attention. Keep it that way.'));
  }
  if (s.accounts > 2) steps.push(tn('You traded {n} accounts. Fewer accounts means one loss limit to watch and your full attention.', { n: s.accounts }));
  const target = best && losing.length ? tn('Only {market} next time, and see the Focus grade go to an A.', { market: names(best.market) }) : tn('Keep one main market.');
  return { evidence, steps, target, actions };
}

const PLANS = { results, risk, discipline, emotions, journaling, focus };

// `ctx` = { month, rules, noJournal, marketName }.
export function improvePlan(id, s, ctx) {
  const plan = PLANS[id];
  if (!plan) return null;
  return plan(s, ctx, ctx.marketName || ((m) => m));
}
